import { Component, OnInit } from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {PagamentoFaturaService} from './payment-invoice.service';
import {InvoiceNew} from '../models/InvoiceNew';
import {MatDialog, MatDialogModule} from '@angular/material/dialog';
import {AppConstants} from '../appConstants';
import {PaymentCheckoutModalComponent, PaymentCheckoutModalData} from "../payment-checkout-modal/payment-checkout-modal.component";
import {lastValueFrom} from "rxjs";
import {AutenticacaoExpressaService} from "../autenticacao-expressa/autenticacao-expressa.service";
import {ModalCreditCardComponent} from "../modal-credit-card/modal-credit-card.component";
import {TrocaObrigatoriaSenhaComponent} from "../troca-obrigatoria-senha/troca-obrigatoria-senha.component";
import {LoginService} from "../login/login.service";
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';

@Component({
  selector: 'app-payment-invoice',
  standalone: true,
  imports: [CommonModule, MatCardModule, MatButtonModule, MatDialogModule, MatIconModule, MatTabsModule, MatMenuModule, MatTooltipModule, MatChipsModule],
  templateUrl: './payment-invoice.component.html',
  styleUrls: ['./payment-invoice.component.css']
})
export class PagamentoFaturaComponent implements OnInit {

  constructor(
      private route: ActivatedRoute,
      private router: Router,
      private service: PagamentoFaturaService,
      private autenticacaoExpressaService: AutenticacaoExpressaService,
      private loginService: LoginService,
      public dialog: MatDialog,
  ) {
    this.id = this.route.snapshot.params["id"];
    this.route.queryParams.subscribe((params) => {
      this.tokenInvoice = params['token'];
    });
  }

  public invoice: InvoiceNew = {} as InvoiceNew;
  public id: number;
  public tokenInvoice: string = "";
  public constants = AppConstants;

  private async verificarTrocaSenha(): Promise<void> {
    try {
      this.service.showSpinner();
      const teste = await lastValueFrom(this.loginService.verificarTrocaSenha());
      this.service.hideSpinner();
      if(teste){
        await lastValueFrom(this.dialog.open(TrocaObrigatoriaSenhaComponent, {
          disableClose: true
        }).afterClosed());
      }
    } catch (e){
      console.log(e);
    } finally {
      this.service.hideSpinner();
    }
  }

  async ngOnInit(): Promise<any> {
    try {
      this.service.showSpinner();
      if(this.tokenInvoice && !this.autenticacaoExpressaService.isTokenAuthenticated(this.tokenInvoice)){
        await this.autenticacaoExpressaService.autenticarToken(this.tokenInvoice);
      }
      await this.loginService.mostrarModalTrocaSenha();
      await this.loadInvoice(true);
    } catch (e) {
      this.service.toasterError('Erro ao gerar faturamento');
      await this.returnToDashboard();
    } finally {
      this.service.hideSpinner();
    }
  }
  async returnToDashboard(): Promise<any> {
    await this.router.navigate(['principal/dashboard']);
  }

  private async loadInvoice(showStatusMessages = false): Promise<void> {
    const res = await lastValueFrom(this.service.getInvoice(this.id));
    this.invoice = res;

    if (!showStatusMessages) {
      return;
    }

    if (res.DATA_PAGAMENTO != null){
      if  (res.DATA_PROCESSAMENTO == null) {
        this.service.toasterSuccess('Esta fatura já consta como paga.' +
          '\n\n' +
          'Aguarde alguns minutos para a atualização de status do seu contrato e dos controles de acesso ' +
          'ao seu box.', 'Parabéns');
      } else {
        this.service.toasterSuccess('Esta fatura já consta como paga.', 'Parabéns');
      }
    } else if (res.ACRESCIMO > 0){
      this.service.toasterWarning('Esta fatura possui acréscimos por estar em atraso. Faça o pagamento para garantir a continuidade do contrato', 'Aviso');
    }
  }

  public formatDate(value: Date | string | null | undefined): string {
    if (!value) {
      return '—';
    }

    return new Intl.DateTimeFormat('pt-BR').format(new Date(value));
  }

  public formatCurrency(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return '—';
    }

    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  }

  public statusLabel(): string {
    if (this.invoice.DATA_PAGAMENTO) {
      return 'Pago';
    }

    if (this.invoice.INADIMPLENTE || this.daysUntilDue() < 0) {
      return 'Vencido';
    }

    return 'A vencer';
  }

  public statusClass(): string {
    if (this.invoice.DATA_PAGAMENTO) {
      return 'status-paid';
    }

    if (this.invoice.INADIMPLENTE || this.daysUntilDue() < 0) {
      return 'status-overdue';
    }

    return 'status-pending';
  }

  public statusHint(): string {
    if (this.invoice.DATA_PAGAMENTO) {
      return `Pago em ${this.formatDate(this.invoice.DATA_PAGAMENTO)}`;
    }

    const diff = this.daysUntilDue();
    if (diff === 0) {
      return 'Vence hoje';
    }

    if (diff > 0) {
      return diff === 1 ? 'Vence em 1 dia' : `Vence em ${diff} dias`;
    }

    const overdueDays = Math.abs(diff);
    return overdueDays === 1 ? '1 dia em atraso' : `${overdueDays} dias em atraso`;
  }

  public statusHintClass(): string {
    if (this.invoice.DATA_PAGAMENTO) {
      return 'due-future';
    }

    const diff = this.daysUntilDue();
    if (diff < 0) {
      return 'due-overdue';
    }
    if (diff === 0) {
      return 'due-today';
    }
    if (diff <= 5) {
      return 'due-soon';
    }

    return 'due-future';
  }

  public isDiscountItem(item: any): boolean {
    const description = String(item?.DESCRICAO_LANCAMENTO || '').toLowerCase();
    return (item?.VALOR_DESCONTO ?? 0) > 0 || description.includes('desconto');
  }

  public itemAmountLabel(item: any): string {
    const baseValue = this.invoice.DATA_PAGAMENTO
      ? item.VALOR_REAL
      : (item.VENCIDO ? item.VALOR_PREVISAO : item.VALOR_PREVISAO_LIQUIDO);

    const formattedValue = this.formatCurrency(baseValue);
    return this.isDiscountItem(item) && baseValue > 0 ? `-${formattedValue}` : formattedValue;
  }

  public get invoiceUnitLabel(): string {
    return this.invoice.DESCRICAO_LOJA;
  }

  public get boxLabel(): string {
    return this.invoice.CODIGO_BOX;
  }

  public get invoiceContractLabel(): string {
    return this.invoice.GEN_CONTRATO;
  }

  async openCreditCardModal(): Promise<any>{
    try {
      await lastValueFrom(this.dialog.open(ModalCreditCardComponent, {
          width: 'min(1240px, calc(100vw - 48px))',
          maxWidth: '100vw',
          maxHeight: '100dvh',
          panelClass: 'credit-card-dialog-panel',
          data: this.invoice,
        }).afterClosed()
      );
    } catch (e: any){
      this.service.toasterError(e.error);
    } finally {
      this.service.showSpinner();
      await this.loadInvoice();
      this.service.hideSpinner();
    }
  }

  async openPixModal(): Promise<any>{
    await this.openCheckoutModal(AppConstants.PAYMENT_METHOD.PIX, 'pix');
  }

  async openBoletoModal(): Promise<any>{
    if (this.invoice.CODIGO_BOLETO) {
      await this.openCheckoutModal(AppConstants.PAYMENT_METHOD.BOLETO, 'boleto', false);
      return;
    }

    await this.openCheckoutModal(AppConstants.PAYMENT_METHOD.BOLETO, 'boleto');
  }

  private async openCheckoutModal(paymentType: number, mode: 'pix' | 'boleto', shouldGeneratePayment = true): Promise<void> {
    try {
      this.service.showSpinner();
      const checkoutConfigResponse = shouldGeneratePayment
        ? await lastValueFrom(this.service.proccessPaymentCheckout(this.id, paymentType))
        : undefined;

      if (mode === 'pix' && !checkoutConfigResponse?.PixQrCodeResponse) {
        this.service.toasterError('Não foi possível gerar o Pix para esta cobrança.');
        return;
      }

      if (mode === 'boleto' && shouldGeneratePayment && !checkoutConfigResponse?.BoletoCheckoutResponse) {
        this.service.toasterError('Não foi possível gerar o boleto para esta cobrança.');
        return;
      }

      this.service.hideSpinner();
      await lastValueFrom(this.dialog.open(PaymentCheckoutModalComponent, {
        width: 'min(96vw, 900px)',
        maxWidth: '96vw',
        data: {
          mode,
          checkoutConfigResponse,
          invoice: this.invoice,
        } as PaymentCheckoutModalData,
      }).afterClosed());
    } catch (err: any) {
      this.service.toasterError(err?.error ?? 'Houve um erro ao preparar o pagamento.');
    } finally {
      this.service.showSpinner();
      await this.loadInvoice();
      this.service.hideSpinner();
    }
  }

  disclaimerAcrescimos(): void {
    this.service.toasterInfo('Eventuais juros e multas de lançamentos atrasados', 'Informaçāo');
  }

  showNfse(): void {

  }

  private daysUntilDue(): number {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const dueDate = new Date(this.invoice.DATA_VENCIMENTO);
    dueDate.setHours(0, 0, 0, 0);

    return Math.round((dueDate.getTime() - today.getTime()) / 86400000);
  }
}
